import { Track } from "@/src/lib/tracking";
/**
 * Batch Add Assets Handler - 批量添加资产处理器
 *
 * 负责处理 batchAddAssets API 的完整逻辑
 */

import type { FavoritesCenterState, OperationResult } from "../types";

import { OptimisticUpdateManager } from "../managers/OptimisticUpdateManager";
import type { PositionsAccountService } from "../PositionsAccountService";
import { UserStateManager } from "../managers/UserStateManager";
import { batchUpdateVersionedData } from "../managers/VersionedDataManager";
import type { AddPositionAsset } from "@features/api/memberApi";

export class BatchAddAssetsHandler {
  constructor(
    private userStateManager: UserStateManager,
    private optimisticUpdateManager: OptimisticUpdateManager,
    private state: FavoritesCenterState,
    private positionsAccountService: PositionsAccountService | null,
    private getCurrentUserId: () => string | null,
    private updateUserFoldersAssetCount: (
      folderId: string,
      action: "ADD" | "REMOVE",
      count?: number,
    ) => void,
  ) {}

  /**
   * Execute batch add assets
   */
  async execute(
    assets: AddPositionAsset[],
    folderId: string,
  ): Promise<OperationResult> {
    try {
      // 1. Parameter validation
      if (!assets || assets.length === 0) {
        return {
          success: false,
          error: "Empty assets array",
        };
      }

      // 2. Ensure user is logged in
      const currentUserId = this.getCurrentUserId();
      if (!currentUserId) {
        return {
          success: false,
          error: "User not logged in",
        };
      }

      // 3. Deduplicate processing (based on asset_id)
      const uniqueAssets = this.deduplicateAssets(assets);

      // 4. Optimistic update: batch update local state
      const updates = this.batchOptimisticUpdate(uniqueAssets, folderId);

      if (updates.length === 0) {
        // All content already exists, no need to update
        return {
          success: true,
          data: { count: 0, message: "All items already favorited" },
        };
      }

      // 5. Batch update state
      batchUpdateVersionedData(this.state.favoriteStates, updates, true);

      // 6. Update source data
      const userData = this.userStateManager.getOrCreateUserData(currentUserId);
      updates.forEach(({ key, value }) => {
        userData.favoriteStates.set(key, value);
      });

      // 7. Update allFavoriteData
      this.optimisticUpdateManager.updateAllFavoriteData(updates);

      // 8. Batch update asset_count
      this.updateUserFoldersAssetCount(folderId, "ADD", updates.length);

      // 9. Call API (no debounce, direct call)
      const assetsToAdd = uniqueAssets.filter((asset) =>
        updates.some((u) => u.key === asset.asset_id),
      );
      const result = await this.batchAddApiCall(assetsToAdd, folderId);

      if (result.success) {
        userData.lastAccess = Date.now();
        Track.log(
          "info",
          "BatchAddAssetsHandler",
          `Batch add favorites completed: ${updates.length} items to ${folderId}`,
        );
        return {
          success: true,
          data: { count: updates.length },
        };
      } else {
        // API failed, rollback all state
        this.batchRollbackFavoriteStatus(
          updates.map((u) => u.key),
          folderId,
        );
        return result;
      }
    } catch (error) {
      // Exception case, rollback all state
      const contentIds = assets.map((a) => a.asset_id);
      this.batchRollbackFavoriteStatus(contentIds, folderId);
      Track.error(error, {
        tag: "BatchAddAssetsHandler",
        msg: "Batch add favorites failed:",
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Deduplicate assets (based on asset_id)
   */
  private deduplicateAssets(assets: AddPositionAsset[]): AddPositionAsset[] {
    const seen = new Set<string>();
    const unique: AddPositionAsset[] = [];

    for (const asset of assets) {
      if (!seen.has(asset.asset_id)) {
        seen.add(asset.asset_id);
        unique.push(asset);
      }
    }

    return unique;
  }

  /**
   * Batch optimistic update favorite status
   */
  private batchOptimisticUpdate(
    assets: AddPositionAsset[],
    folderId: string,
  ): Array<{ key: string; value: string[] }> {
    const updates: Array<{ key: string; value: string[] }> = [];

    assets.forEach((asset) => {
      const contentId = asset.asset_id;
      const currentState = this.state.favoriteStates.data[contentId] || [];

      // If already included, skip
      if (currentState.includes(folderId)) {
        return;
      }

      // Add new folder ID
      const newState = [...currentState, folderId];
      updates.push({ key: contentId, value: newState });
    });

    return updates;
  }

  /**
   * Batch add API call
   */
  private async batchAddApiCall(
    assets: AddPositionAsset[],
    folderId: string,
  ): Promise<OperationResult<{ count: number; message?: string }>> {
    if (!this.positionsAccountService) {
      return {
        success: false,
        error: "PositionsAccountService not initialized",
      };
    }

    try {
      const result = await this.positionsAccountService.addUserPosition(
        folderId,
        assets,
      );

      if (result.success) {
        return {
          success: true,
          data: { count: assets.length },
        };
      } else {
        return {
          success: false,
          error: result.message || "Failed to add favorites",
        };
      }
    } catch (error) {
      Track.error(error, {
        tag: "BatchAddAssetsHandler",
        msg: "Batch add API call failed:",
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Batch rollback favorite status
   */
  private batchRollbackFavoriteStatus(
    contentIds: string[],
    folderId: string,
  ): void {
    const updates = contentIds.map((contentId) => {
      const currentState = this.state.favoriteStates.data[contentId] || [];
      const revertedState = currentState.filter((id) => id !== folderId);
      return { key: contentId, value: revertedState };
    });

    // Rollback state
    batchUpdateVersionedData(this.state.favoriteStates, updates, true);

    // Rollback source data
    const currentUserId = this.getCurrentUserId();
    if (currentUserId) {
      const userData = this.userStateManager.getOrCreateUserData(currentUserId);
      updates.forEach(({ key, value }) => {
        userData.favoriteStates.set(key, value);
      });
    }

    // Rollback allFavoriteData
    this.optimisticUpdateManager.updateAllFavoriteData(updates);

    // Rollback asset_count
    this.updateUserFoldersAssetCount(folderId, "REMOVE", contentIds.length);

    Track.log(
      "info",
      "BatchAddAssetsHandler",
      `Rolled back batch add: ${contentIds.length} items from ${folderId}`,
    );
  }
}
